import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * Public landing page used as the `redirect_uri` for Google / Apple OAuth.
 *
 * Why this exists: pointing OAuth directly at `/` (a protected route) creates a
 * race, during the brief moment before `supabase-js` parses the URL hash and
 * sets the session, `ProtectedRoute` sees `user === null` and bounces the user
 * to `/auth`, which looks exactly like "Google sign-up didn't work" even when
 * the account was actually created. This callback page is public, so it can
 * sit and wait for the session to hydrate, then route the user appropriately.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [message, setMessage] = useState('Finishing sign-in…');

  useEffect(() => {
    // Surface any provider error returned in the URL (e.g. user cancelled,
    // Google rejected the request) so the user sees a real message instead of
    // an unexplained bounce back to the auth page.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search);
    const errorDescription =
      hashParams.get('error_description') || queryParams.get('error_description');
    const errorCode = hashParams.get('error') || queryParams.get('error');

    if (errorCode || errorDescription) {
      toast.error(errorDescription || `Google sign-in failed (${errorCode})`);
      navigate('/auth', { replace: true });
      return;
    }

    let done = false;

    const finish = (path: string) => {
      if (done) return;
      done = true;
      navigate(path, { replace: true });
    };

    // Listen for the session being established by supabase-js after it parses
    // the access_token / refresh_token from the URL hash.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        finish('/');
      } else if (event === 'SIGNED_OUT') {
        finish('/auth');
      }
    });

    // Also check whether a session is already present (e.g. tokens were
    // restored from storage before the listener attached).
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) finish('/');
    });

    // Safety net: if neither path resolves within 8s something is wrong with
    // the OAuth exchange, send the user back to the auth page with a hint.
    const timeout = window.setTimeout(() => {
      if (!done) {
        setMessage('Still working on it…');
      }
    }, 3000);
    const hardTimeout = window.setTimeout(() => {
      if (!done) {
        toast.error("Sign-in didn't complete. Please try again.");
        finish('/auth');
      }
    }, 8000);

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(timeout);
      window.clearTimeout(hardTimeout);
    };
  }, [navigate]);

  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background p-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground">{message}</p>
    </main>
  );
}